import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteKanbanPageAction } from '../actions/deleteKanbanPage.action';
import { KanbanPage } from '../interfaces/kanbanPage.interface';
import { FlowTask } from '../interfaces/flowTask.interface';
import { useAppDispatch } from '../../../redux/hooks/redux';
import { fetchBenefits } from '../../../permissions/benefits/redux/benefits.thunks';

export const useDeleteKanbanPage = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (pageId: string) => deleteKanbanPageAction(pageId),
    onMutate: async (pageId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['kanban-pages'] });
      await queryClient.cancelQueries({ queryKey: ['todo-tasks', pageId] });

      // Snapshot the previous values
      const previousPages = queryClient.getQueryData<KanbanPage[]>(['kanban-pages']);
      const previousTasks = queryClient.getQueryData<FlowTask[]>(['todo-tasks', pageId]);

      // Optimistically update pages
      if (previousPages) {
        queryClient.setQueryData<KanbanPage[]>(
          ['kanban-pages'],
          previousPages.filter((page) => page.uid !== pageId)
        );
      }

      // Optimistically remove tasks for this page
      queryClient.setQueryData<FlowTask[]>(['todo-tasks', pageId], []);

      return { previousPages, previousTasks };
    },
    onError: (err, pageId, context) => {
      // Rollback optimistic updates
      if (context?.previousPages) {
        queryClient.setQueryData(['kanban-pages'], context.previousPages);
      }
      if (context?.previousTasks) {
        queryClient.setQueryData(['todo-tasks', pageId], context.previousTasks);
      }
    },
    onSettled: (data, error, pageId) => {
      dispatch(fetchBenefits());
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: ['kanban-pages'] });
      queryClient.invalidateQueries({ queryKey: ['todo-tasks', pageId] });
      // Also invalidate all todo-tasks queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['todo-tasks'] });
    },
  });
};

